/**
 * Safe localStorage helpers for lightweight UI preferences (Phase 4).
 * Never throws on quota / parse / availability errors — falls back gracefully.
 */
import { logger } from "./logger";

const canUseStorage = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return false;
    }
    const probeKey = "__mis_storage_probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = canUseStorage();

export const storage = {
  /**
   * @param {string} key
   * @param {*} value - JSON-serializable value
   * @returns {boolean} whether the write succeeded
   */
  set(key, value) {
    if (!storageAvailable || !key) {
      return false;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      logger.warn("[storage] set failed:", key, error?.name || error)
      return false;
    }
  },

  /**
   * @param {string} key
   * @param {*} defaultValue
   * @returns {*}
   */
  get(key, defaultValue = null) {
    if (!storageAvailable || !key) {
      return defaultValue;
    }
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null || raw === undefined) {
        return defaultValue;
      }
      return JSON.parse(raw);
    } catch (error) {
      logger.warn("[storage] get/parse failed, using default:", key, error?.name || error)
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore cleanup failure
      }
      return defaultValue;
    }
  },

  /**
   * @param {string} key
   * @returns {boolean}
   */
  remove(key) {
    if (!storageAvailable || !key) {
      return false;
    }
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      logger.warn("[storage] remove failed:", key, error?.name || error)
      return false;
    }
  },

  /**
   * Clears all localStorage keys. Prefer remove() for targeted cleanup.
   * @returns {boolean}
   */
  clear() {
    if (!storageAvailable) {
      return false;
    }
    try {
      window.localStorage.clear();
      return true;
    } catch (error) {
      logger.warn("[storage] clear failed:", error?.name || error)
      return false;
    }
  },
};

export default storage;
